import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, FileText, X } from 'lucide-react';
import { Field, QueryState, Select, formatDate, todayIso, useFormErrors } from '@/components/live';
import { DropZone } from '@/components/DropZone';
import { DeleteRecordButton } from '@/components/DeleteRecordButton';
import { ValidityBadge } from '@/components/ValidityBadge';
import { NameVerificationLine } from '@/components/NameVerificationLine';
import { namesDiffer } from '@/lib/nameMatch';
import { NameMatchBasisSelect } from '@/components/NameMatchBasisSelect';
import type { FarmerRow } from '@/types/records';
import { createRow, saveRow } from '@/api/resources';
import { referenceValues } from '@/api/lookups';
import { documentTypes, signedUrlsFor, uploadDocument } from '@/api/documents';
import { useRows } from '@/hooks/useResource';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { toast } from '@/lib/toast';
import { isSupabaseConfigured } from '@/lib/supabase';

interface LandDocumentRow {
  id: string;
  land_document_code: string | null;
  plot_id: string;
  land_document_type_id: string;
  land_document_type: string | null;
  document_no: string | null;
  issued_on: string | null;
  document_id: string | null;
  file_name: string | null;
  /** Files after the first (migration 0165): the other pages, a registration receipt ... */
  extra_document_ids: string[] | null;
  notes: string | null;
  valid_from: string | null;
  valid_to: string | null;
  name_on_document: string | null;
  name_match_basis: string | null;
  name_match_remarks: string | null;
}

const EMPTY = {
  land_document_type_id: '', document_no: '', issued_on: '', notes: '', valid_from: '', valid_to: '',
  name_on_document: '', name_match_basis: '', name_match_remarks: '',
  // A deed does not expire; a record of rights is revised (owner, 2026-09-27: "add validity as
  // toggle and display fields on selection?"). Off: no validity dates are asked or stored.
  has_validity: false,
};

/**
 * The land documents of one plot (owner, 2026-09-21: "land document number and land document
 * type ... uploading land documents - at plot level"): the record of rights, the deed, the
 * mutation - each with its type, number, issue date and the scanned file (photo or PDF).
 */
/**
 * A land document added to a plot that is not saved yet (owner, 2026-09-27: "no option to add the
 * file"). It is held in the form and written by `saveLandDocument` once the plot exists.
 */
export interface PendingLandDocument {
  key: string;
  typeLabel: string;
  values: Record<string, unknown>;
  files: File[];
  documentTypeId: string | null;
}

/**
 * Uploads the files and records the land document against a saved plot. The first file is the
 * document's own; the rest are its further pages or attachments.
 */
export async function saveLandDocument(plotId: string, organizationId: string, item: Omit<PendingLandDocument, 'key' | 'typeLabel'>) {
  const ids: string[] = [];
  for (const file of item.files) {
    const uploaded = await uploadDocument({
      file, bucket: 'plot-evidence', organizationId, ownerId: plotId,
      documentTypeId: item.documentTypeId, issuedDate: (item.values.issued_on as string | null) ?? null,
    });
    ids.push(uploaded.id);
  }
  await createRow('plot_land_documents', {
    ...item.values, plot_id: plotId, document_id: ids[0] ?? null, extra_document_ids: ids.slice(1),
  });
}

export function PlotLandDocuments({ plotId, farmerId, pending, onPendingChange, onDirtyChange, onNameCheckRaised, children }: {
  /** A saved document names its holder differently from the ID card: a name verification now exists. */
  onNameCheckRaised?: () => void;
  /** Told whether a document is filled in but not yet saved, so the plot's save can say so. */
  onDirtyChange?: (dirty: boolean) => void;
  /** The plot's own land fields - tenure, lease, declared area - shown inside this section, above
   *  the documents (owner, 2026-09-27: "attached fields add inside the land docs"). */
  children?: ReactNode;
  /** Absent for a plot not saved yet: documents are then held in `pending` until it is. */
  plotId?: string | null;
  farmerId?: string | null;
  pending?: PendingLandDocument[];
  onPendingChange?: (pending: PendingLandDocument[]) => void;
}) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const { errors, check, clear } = useFormErrors();
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const mayAdd = can(profile, 'plot.mapping', 'create');
  // The add form is always open for someone who may add (owner, 2026-09-27: "default show - add
  // land document feilds, no need to have button?"); saving clears it for the next document.
  const adding = mayAdd;
  const dirty = files.length > 0 || Object.entries(form).some(([key, value]) => key !== 'has_validity' && Boolean(value));
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  const mayDelete = can(profile, 'plot.mapping', 'update');

  const rows = useRows<LandDocumentRow>('plot_land_documents', {
    filters: { plot_id: plotId ?? undefined }, orderBy: { column: 'created_at', ascending: true },
  }, Boolean(plotId));
  const types = useQuery({
    queryKey: ['ref', 'land_document_type'], queryFn: () => referenceValues('land_document_type'),
    enabled: isSupabaseConfigured && adding,
  });
  // The holder's name on the record should be the farmer's name as per ID card (0078); if it is
  // written differently, saving opens a name verification for the party and this document.
  const farmer = useRows<FarmerRow>('farmers', { filters: { id: farmerId } }, Boolean(farmerId) && adding);
  const farmerIdName = farmer.data?.[0] ? (farmer.data[0].name_as_per_id || farmer.data[0].full_name) : null;
  const holderDiffers = Boolean(farmerIdName) && namesDiffer(form.name_on_document, farmerIdName);
  const docTypes = useQuery({ queryKey: ['document_types'], queryFn: documentTypes, enabled: isSupabaseConfigured && adding });
  const fileIds = (rows.data ?? []).flatMap((row) => [row.document_id, ...(row.extra_document_ids ?? [])]).filter((id): id is string => Boolean(id));
  const links = useQuery({
    queryKey: ['signed-urls', ...fileIds], queryFn: () => signedUrlsFor(fileIds), enabled: fileIds.length > 0,
  });

  function reset() {
    setForm(EMPTY);
    setFiles([]);
    clear();
  }

  function pickFile(picked: File) {
    if (!picked.type.startsWith('image/') && picked.type !== 'application/pdf') {
      toast.error(`${picked.name}: choose a photograph or a PDF of the document.`);
      return;
    }
    setFiles((now) => now.some((file) => file.name === picked.name && file.size === picked.size) ? now : [...now, picked]);
  }

  async function save() {
    const ok = check({
      land_document_type_id: form.land_document_type_id ? undefined : 'Which document is it?',
      document_no: form.document_no.trim() || files.length > 0 ? undefined : 'Give the document number, or attach the document.',
      valid_to: !form.has_validity ? undefined
        : !form.valid_to ? 'Until when is the document valid?'
        : (form.valid_from || form.issued_on) && form.valid_to <= (form.valid_from || form.issued_on) ? 'It must end after it starts.' : undefined,
    });
    if (!ok || !profile?.organization) return;
    const item = {
      files,
      documentTypeId: docTypes.data?.find((row) => row.document_type_code === 'LAND_RTC')?.id ?? null,
      values: {
        land_document_type_id: form.land_document_type_id,
        document_no: form.document_no.trim() || null,
        issued_on: form.issued_on || null,
        valid_from: form.has_validity ? form.valid_from || form.issued_on || null : null,
        valid_to: form.has_validity ? form.valid_to : null,
        name_on_document: form.name_on_document.trim() || null,
        name_match_basis: holderDiffers ? form.name_match_basis || null : null,
        name_match_remarks: holderDiffers ? form.name_match_remarks.trim() || null : null,
        notes: form.notes.trim() || null,
      },
    };
    if (!plotId) {
      // Nothing to attach to yet: held with the form and saved with the plot.
      onPendingChange?.([...(pending ?? []), {
        ...item, key: crypto.randomUUID(),
        typeLabel: types.data?.find((row) => row.id === form.land_document_type_id)?.label ?? 'Document',
      }]);
      toast.success('Land document added. It is saved with the plot.');
      reset();
      return;
    }
    setSaving(true);
    try {
      await saveLandDocument(plotId, profile.organization.id, item);
      await queryClient.invalidateQueries({ queryKey: ['plot_land_documents'] });
      if (holderDiffers) {
        await queryClient.invalidateQueries({ queryKey: ['name_verifications'] });
        onNameCheckRaised?.();
      }
      toast.success('Land document added.');
      reset();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="plot-land-documents">
      <div className="section-heading">
        <h3>Land documents <span className="muted">({plotId ? rows.data?.length ?? 0 : pending?.length ?? 0})</span></h3>
      </div>

      {children}

      {adding && (
        <div className="inline-form">
          <div className="field-row">
            <Field error={errors.land_document_type_id} label="Document type" required>
              <Select onChange={(value) => setForm({ ...form, land_document_type_id: value })}
                      options={(types.data ?? []).map((row) => ({ id: row.id, label: row.label }))}
                      placeholder={types.isLoading ? 'Loading...' : 'Select type'} value={form.land_document_type_id} />
            </Field>
            <Field error={errors.document_no} hint="Khatian, khasra, deed or survey number." label="Document number">
              <input onChange={(event) => setForm({ ...form, document_no: event.target.value })} value={form.document_no} />
            </Field>
          </div>
          <Field hint="The holder exactly as named on the record - often a parent or co-sharer." label="Name on document (holder)">
            <input onChange={(event) => setForm({ ...form, name_on_document: event.target.value })}
                   placeholder={farmerIdName ?? undefined} value={form.name_on_document} />
          </Field>
          {holderDiffers && (
            <div className="name-match">
              <p className="muted" style={{ margin: 0 }}>
                The record names <strong>{form.name_on_document}</strong>; the farmer's name as per ID card is <strong>{farmerIdName}</strong>.
                Saving opens a <strong>name verification</strong> for someone else to accept. Add what confirms it, if you know.
              </p>
              <Field label="Confirmed by">
                <NameMatchBasisSelect onChange={(value) => setForm((now) => ({ ...now, name_match_basis: value }))} recorded={form.name_on_document} reference={farmerIdName} value={form.name_match_basis} />
              </Field>
              <Field label="Remarks">
                <input onChange={(event) => setForm({ ...form, name_match_remarks: event.target.value })}
                       placeholder="e.g. Khatian in father's name; mutation pending" value={form.name_match_remarks} />
              </Field>
            </div>
          )}
          <div className="field-row">
            <Field label="Issued on">
              <input max={todayIso()} onChange={(event) => setForm({ ...form, issued_on: event.target.value })} type="date" value={form.issued_on} />
            </Field>
            <Field hint={form.has_validity ? 'It is revised or renewed; give its dates below.' : 'A deed that does not expire.'} label="Validity period">
              <div aria-label="Validity period" className="view-toggle" role="group">
                {([[true, 'Has validity'], [false, 'No expiry']] as const).map(([value, label]) => (
                  <button aria-pressed={form.has_validity === value} key={label} type="button"
                          onClick={() => setForm({ ...form, has_validity: value, ...(value ? {} : { valid_from: '', valid_to: '' }) })}>
                    {label}
                  </button>
                ))}
              </div>
            </Field>
          </div>
          {form.has_validity && (
            <div className="field-row">
              <Field hint="Blank: from the issue date." label="Valid from">
                <input onChange={(event) => setForm({ ...form, valid_from: event.target.value })} type="date" value={form.valid_from} />
              </Field>
              <Field error={errors.valid_to} hint="A record of rights: until its next revision or renewal." label="Valid to" required>
                <input min={form.valid_from || form.issued_on || undefined} onChange={(event) => setForm({ ...form, valid_to: event.target.value })} type="date" value={form.valid_to} />
              </Field>
            </div>
          )}
          <Field hint="Photographs or PDFs - every page, and any receipt that goes with it." label="Files">
            <DropZone accept="image/*,application/pdf" buttonLabel={files.length > 0 ? 'Add more files' : 'Choose files'}
                      label="Drag the files here, or" multiple onFile={pickFile} />
          </Field>
          {files.length > 0 && (
            <ul className="land-document-list">
              {files.map((file, index) => (
                <li key={`${file.name}-${file.size}`}>
                  <FileText size={16} />
                  <span className="land-document-text">
                    <strong>{file.name}</strong>
                    <small className="muted">{index === 0 ? 'first file' : `file ${index + 1}`} · {Math.max(1, Math.round(file.size / 1024))} KB</small>
                  </span>
                  <button onClick={() => setFiles((now) => now.filter((other) => other !== file))} type="button"><X size={14} /> Remove</button>
                </li>
              ))}
            </ul>
          )}
          <Field label="Notes">
            <input onChange={(event) => setForm({ ...form, notes: event.target.value })} value={form.notes} />
          </Field>
          <div className="inline-form-actions">
            <button className="cancel" onClick={reset} type="button">Clear</button>
            <button className="primary" disabled={saving} onClick={() => void save()} type="button">
              {saving ? 'Saving...' : 'Save land document'}
            </button>
          </div>
        </div>
      )}

      {!plotId && (
        (pending ?? []).length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>No land documents yet. Add the record of rights, deed or lease; they are saved with the plot.</p>
        ) : (
          <ul className="land-document-list">
            {(pending ?? []).map((item) => (
              <li key={item.key}>
                <FileText size={18} />
                <span className="land-document-text">
                  <strong>{item.typeLabel}</strong>
                  <small className="muted">
                    {[item.values.document_no ? `No. ${String(item.values.document_no)}` : null,
                      item.values.valid_to ? `valid to ${formatDate(item.values.valid_to as string)}` : 'no expiry',
                      item.files.length === 0 ? 'no file' : item.files.length === 1 ? item.files[0]!.name : `${item.files.length} files`,
                      'saved with the plot'].filter(Boolean).join(' · ')}
                  </small>
                </span>
                <button onClick={() => onPendingChange?.((pending ?? []).filter((other) => other.key !== item.key))} type="button">
                  <X size={14} /> Remove
                </button>
              </li>
            ))}
          </ul>
        )
      )}
      {plotId && (
      <QueryState empty="No land documents yet. Add the record of rights, deed or lease for this plot." query={rows}>
        <ul className="land-document-list">
          {(rows.data ?? []).map((row) => (
            <li key={row.id}>
              <FileText size={18} />
              <span className="land-document-text">
                <strong>{row.land_document_type ?? 'Document'} {row.valid_to && <ValidityBadge compact from={row.valid_from} to={row.valid_to} />}</strong>
                <small className="muted">
                  {[row.document_no ? `No. ${row.document_no}` : null, row.issued_on ? `issued ${formatDate(row.issued_on)}` : null,
                    row.valid_to ? `valid to ${formatDate(row.valid_to)}` : 'no expiry', row.name_on_document ? `holder: ${row.name_on_document}` : null, row.notes]
                    .filter(Boolean).join(' · ') || row.land_document_code}
                </small>
                <NameVerificationLine entityId={row.id} entityTable="ops.plot_land_documents" />
              </span>
              {[row.document_id, ...(row.extra_document_ids ?? [])].filter((id): id is string => Boolean(id)).map((id, index, all) => (
                links.data?.[id] ? (
                  <a href={links.data[id]} key={id} rel="noreferrer" target="_blank" title={index === 0 ? row.file_name ?? 'Open' : `File ${index + 1}`}>
                    <ExternalLink size={15} /> {all.length > 1 ? `File ${index + 1}` : 'Open'}
                  </a>
                ) : null
              ))}
              {mayDelete && (
                <DeleteRecordButton
                  deleteFn={(reason) => saveRow('plot_land_documents', row.id, { is_deleted: true, delete_reason: reason })}
                  entity="land_document" id={row.id} invalidate={['plot_land_documents']}
                  label={`${row.land_document_type ?? 'land document'} ${row.document_no ?? ''}`.trim()} onDeleted={() => undefined} screenKey="plot.mapping" />
              )}
            </li>
          ))}
        </ul>
      </QueryState>
      )}
    </section>
  );
}
